import React, { useState, useEffect, useRef } from 'react';
import SearchBar from './SearchBar';
import { useIsMobile } from '../hooks/useIsMobile';
import ConfirmModal from './ConfirmModal';
import SupplementFormModal from './SupplementFormModal';
import { SupplementCatalog } from '../models/diary';
import { supplementCatalogService, CreateSupplementData } from '../services/supplementCatalogService';

const SupplementCatalogList: React.FC = () => {
  const [items, setItems] = useState<SupplementCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editItem, setEditItem] = useState<SupplementCatalog | undefined>(undefined);
  const [detailItem, setDetailItem] = useState<SupplementCatalog | null>(null);
  const [deleteItem, setDeleteItem] = useState<SupplementCatalog | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.values(menuRefs.current).every(
        ref => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutside) setActiveMenu(null);
    };
    if (activeMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  const fetchItems = async (page: number = 1, searchQuery: string = search) => {
    try {
      setLoading(true);
      const response = await supplementCatalogService.getAll(page, 10, searchQuery);
      setItems(response.results);
      setCurrentPage(response.actualPage);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to fetch supplements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search: reset to page 1 and fetch when query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchItems(1, search);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCreate = async (data: Partial<CreateSupplementData>) => {
    await supplementCatalogService.create(data as CreateSupplementData);
    setShowCreateModal(false);
    await fetchItems(1);
    showSuccess('Suplemento creado correctamente.');
  };

  const handleEdit = async (data: Partial<CreateSupplementData>) => {
    if (!editItem) return;
    await supplementCatalogService.update(editItem.id, data);
    setEditItem(undefined);
    await fetchItems(currentPage);
    showSuccess('Suplemento actualizado correctamente.');
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await supplementCatalogService.remove(deleteItem.id);
      setDeleteItem(null);
      setDeleteError('');
      await fetchItems(currentPage);
      showSuccess('Suplemento eliminado correctamente.');
    } catch (error: any) {
      const message =
        error.response?.error ||
        error.message ||
        'Error al eliminar el suplemento.';
      setDeleteError(message);
      console.error('Failed to delete supplement:', error);
    }
  };

  const truncate = (text: string, max: number) =>
    text && text.length > max ? text.substring(0, max) + '...' : text || '-';

  return (
    <div>
      {successMessage && (
        <div style={{
          background: '#dcfce7', color: '#15803d',
          padding: '12px 16px', borderRadius: 8,
          marginBottom: 16, fontSize: 14, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>&#10003;</span>
          {successMessage}
        </div>
      )}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: 24,
      }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por nombre o descripcion" fullWidth={isMobile} />
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)', transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#319795';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(56, 178, 172, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#38b2ac';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
          <span>Nuevo Suplemento</span>
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
          Cargando suplementos...
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
          No se encontraron suplementos
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>NOMBRE</th>
                {!isMobile && (
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>DESCRIPCION</th>
                )}
                {!isMobile && (
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>URL DE COMPRA</th>
                )}
                <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={item.id}
                  style={{
                    borderBottom: index < items.length - 1 ? '1px solid #f1f5f9' : 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => setDetailItem(item)}
                >
                  <td style={{ padding: '16px 20px', fontWeight: 600, color: '#1f2937', fontSize: 15 }}>
                    {item.name}
                  </td>
                  {!isMobile && (
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                      {truncate(item.description, 60)}
                    </td>
                  )}
                  {!isMobile && (
                    <td style={{ padding: '16px 20px', fontSize: 14 }}>
                      {item.purchaseUrl ? (
                        <a
                          href={item.purchaseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#38b2ac', textDecoration: 'none' }}
                          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                        >
                          {truncate(item.purchaseUrl, 40)}
                        </a>
                      ) : (
                        <span style={{ color: '#6b7280' }}>-</span>
                      )}
                    </td>
                  )}
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <div
                      ref={(el) => { menuRefs.current[item.id] = el; }}
                      style={{ position: 'relative', display: 'inline-block' }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === item.id ? null : item.id);
                        }}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          padding: '8px', fontSize: '24px', color: '#555', lineHeight: 1,
                          borderRadius: '50%', width: '36px', height: '36px',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        &#8942;
                      </button>
                      {activeMenu === item.id && (
                        <div style={{
                          position: 'absolute', right: 0, top: '100%', marginTop: '4px',
                          background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          borderRadius: '8px', minWidth: '180px', padding: '8px 0', zIndex: 100,
                        }}>
                          <button
                            style={{
                              width: '100%', padding: '12px 16px', border: 'none',
                              background: 'transparent', textAlign: 'left', cursor: 'pointer',
                              fontSize: '15px', color: '#222', transition: 'background 0.2s',
                            }}
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setDetailItem(item); }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            Ver detalle
                          </button>
                          <button
                            style={{
                              width: '100%', padding: '12px 16px', border: 'none',
                              background: 'transparent', textAlign: 'left', cursor: 'pointer',
                              fontSize: '15px', color: '#222', transition: 'background 0.2s',
                            }}
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setEditItem(item); }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            Editar
                          </button>
                          <button
                            style={{
                              width: '100%', padding: '12px 16px', border: 'none',
                              background: 'transparent', textAlign: 'left', cursor: 'pointer',
                              fontSize: '15px', color: '#e53e3e', transition: 'background 0.2s',
                            }}
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setDeleteItem(item); }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#fff5f5')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && items.length > 0 && totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
          marginTop: '24px', padding: '16px 0',
        }}>
          <button
            onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchItems(p, search); }}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
              background: currentPage === 1 ? '#f8fafc' : '#fff',
              color: currentPage === 1 ? '#94a3b8' : '#1f2937',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: 500, fontSize: '14px', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { if (currentPage !== 1) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
            onMouseLeave={(e) => { if (currentPage !== 1) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
          >
            Anterior
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const isCurrentPage = page === currentPage;
              const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
              if (!showPage) {
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>;
                }
                return null;
              }
              return (
                <button
                  key={page}
                  onClick={() => { setCurrentPage(page); fetchItems(page, search); }}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                    background: isCurrentPage ? '#38b2ac' : '#fff',
                    color: isCurrentPage ? '#fff' : '#1f2937',
                    cursor: 'pointer', fontWeight: isCurrentPage ? 600 : 500,
                    fontSize: '14px', minWidth: '36px', transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { if (!isCurrentPage) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                  onMouseLeave={(e) => { if (!isCurrentPage) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchItems(p, search); }}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
              background: currentPage === totalPages ? '#f8fafc' : '#fff',
              color: currentPage === totalPages ? '#94a3b8' : '#1f2937',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: 500, fontSize: '14px', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
            onMouseLeave={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setDetailItem(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: 32,
              maxWidth: 560, width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: 0, marginBottom: 20, fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
              {detailItem.name}
            </h2>
            {detailItem.description && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#374151', display: 'block', marginBottom: 4 }}>Descripcion</span>
                <p style={{ margin: 0, fontSize: 15, color: '#6b7280', lineHeight: 1.6 }}>{detailItem.description}</p>
              </div>
            )}
            {detailItem.purchaseUrl && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#374151', display: 'block', marginBottom: 4 }}>URL de compra</span>
                <a
                  href={detailItem.purchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#38b2ac', fontSize: 15, wordBreak: 'break-all' }}
                >
                  {detailItem.purchaseUrl}
                </a>
              </div>
            )}
            <div style={{ marginBottom: 24, fontSize: 13, color: '#9ca3af' }}>
              Creado: {new Date(detailItem.created_at).toLocaleDateString()} &middot; Actualizado: {new Date(detailItem.updated_at).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDetailItem(null); setDeleteItem(detailItem); }}
                style={{
                  background: '#fee2e2', color: '#e53e3e', border: 'none', borderRadius: 6,
                  padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fecaca'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
              >
                Eliminar
              </button>
              <button
                onClick={() => { setDetailItem(null); setEditItem(detailItem); }}
                style={{
                  background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <SupplementFormModal
        open={showCreateModal || !!editItem}
        item={editItem}
        onSave={editItem ? handleEdit : handleCreate}
        onCancel={() => { setShowCreateModal(false); setEditItem(undefined); }}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        open={!!deleteItem}
        title="Eliminar suplemento"
        message={
          <div>
            <span>{`¿Estás seguro de que deseas eliminar "${deleteItem?.name}"? Esta acción no se puede deshacer.`}</span>
            {deleteError && (
              <div style={{
                marginTop: 12,
                background: '#fee2e2', color: '#dc2626',
                padding: '10px 14px', borderRadius: 6, fontSize: 14,
              }}>
                {deleteError}
              </div>
            )}
          </div>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => { setDeleteItem(null); setDeleteError(''); }}
      />
    </div>
  );
};

export default SupplementCatalogList;
